import React, { useEffect, useRef } from "react";
import * as echarts from "echarts";
import { Label } from "reactstrap";
import IncomeAndExpense from "./IncomeAndExpense";
import { useSelector } from "react-redux";
const Dashboard: React.FC = () => {
    // Create references for the chart containers
    const incomeChartRef = useRef<HTMLDivElement | null>(null);
    const expenseChartRef = useRef<HTMLDivElement | null>(null);
    const savingsChartRef = useRef<HTMLDivElement | null>(null);
    const dashboardData = useSelector((state: any) => state.dashboard.dashboardData);

    useEffect(() => {
        if (incomeChartRef.current && expenseChartRef.current && savingsChartRef.current) {
            // New categories with random values for the Income chart
            const incomeAndExpenseCategories = [
                { value: dashboardData[4]?.amount || 0, name: "Income", itemStyle: { color: "#27ae60" } },
                { value: dashboardData[3]?.amount || 0, name: "Expense", itemStyle: { color: "#e74c3c" } },
            ];

            const bedDebtAndGoodDebt = [
                { value: dashboardData[0]?.amount || 0, name: "Bad Debt  ", itemStyle: { color: "#e74c3c", } },
                { value: dashboardData[1]?.amount || 0, name: "Good Debt", itemStyle: { color: "#27ae60" } },
            ];

            const assetsAndLiabilities = [
                { value: dashboardData[6]?.amount || 0, name: "Total Assets  ", itemStyle: { color: "#27ae60" } },
                { value: dashboardData[7]?.amount || 0, name: "Total Liabilities", itemStyle: { color: "#e74c3c" } },
            ];

            // Define chart options for Income with new categories
            const incomeOptions = {
                title: {
                    text: "",
                    left: "center",
                    top: "top",
                },
                tooltip: {
                    trigger: "item",
                },
                legend: {
                    orient: "horizontal",
                    left: "center",
                },
                series: [
                    {
                        name: "Income & Expense",
                        type: "pie",
                        radius: ["40%", "70%"],
                        data: incomeAndExpenseCategories,
                        label: {
                            show: false,
                            position: "inside",
                            formatter: "{b}: {c}",
                        },
                        itemStyle: {
                            borderWidth: 0,
                        },
                    },
                ],
            };

            // Retain the previous data for Expenses and Savings charts
            const expenseOptions = {
                title: {
                    text: "",
                    left: "center",
                    top: "top",
                },
                tooltip: {
                    trigger: "item",
                },
                legend: {
                    orient: "horizontal",
                    left: "center",
                },
                series: [
                    {
                        name: "Bad Debt / Good Debt",
                        type: "pie",
                        radius: ["40%", "70%"], // Create doughnut chart
                        data: bedDebtAndGoodDebt,
                        label: {
                            show: false,
                            position: "inside", // Labels inside the chart
                            formatter: "{b}: {c}", // Show label name and value
                        },
                        itemStyle: {
                            borderWidth: 0, // Remove border
                        },
                    },
                ],
            };

            const savingsOptions = {
                title: {
                    text: "",
                    left: "center",
                    top: "top",
                },
                tooltip: {
                    trigger: "item",
                },
                legend: {
                    orient: "horizontal",
                    left: "center",
                },
                series: [
                    {
                        name: "Assets & Liabilities",
                        type: "pie",
                        radius: ["40%", "70%"], // Create doughnut chart
                        data: assetsAndLiabilities,
                        label: {
                            show: false,
                            position: "inside", // Labels inside the chart
                            formatter: "{b}: {c}", // Show label name and value
                        },
                        itemStyle: {
                            borderWidth: 0, // Remove border
                        },
                    },
                ],
            };

            // Initialize the ECharts instances
            const incomeChart = echarts.init(incomeChartRef.current);
            const expenseChart = echarts.init(expenseChartRef.current);
            const savingsChart = echarts.init(savingsChartRef.current);

            // Set options for each chart
            incomeChart.setOption(incomeOptions);
            expenseChart.setOption(expenseOptions);
            savingsChart.setOption(savingsOptions);

            // Cleanup function to avoid memory leaks
            return () => {
                // Dispose charts to clean up resources
                incomeChart.dispose();
                expenseChart.dispose();
                savingsChart.dispose();
            };
        }
    }, [dashboardData]);


    const formatNumberWithCommas = (value: number) => {
        return value?.toLocaleString();
    };

    return (
        <div className="px-4  pt-4">
            <div className="custom-cards">
                <div className="custom-card custom-card-income">
                    <div className="custom-card-header">
                        <i className="fas fa-arrow-circle-up"></i> Income
                    </div>
                    <div className="custom-card-value">${formatNumberWithCommas(dashboardData[4]?.amount || 0)}</div>
                </div>

                <div className="custom-card custom-card-expense">
                    <div className="custom-card-header">
                        <i className="fas fa-arrow-circle-down"></i> Expense
                    </div>
                    <div className="custom-card-value">${formatNumberWithCommas(dashboardData[3]?.amount || 0)}</div>
                </div>

                <div className="custom-card custom-card-cashflow">
                    <div className="custom-card-header">
                        <i className="fas fa-coins"></i> Cashflow
                    </div>
                    <div className="custom-card-value">${formatNumberWithCommas(dashboardData[5]?.amount || 0)}</div>
                </div>

                <div className="custom-card custom-card-profit">
                    <div className="custom-card-header">
                        <i className="fas fa-chart-line"></i> Saving Rate
                    </div>
                    <div className="custom-card-value">
                    {Math.max(((dashboardData[5]?.amount || 0) / (dashboardData[4]?.amount || 1)) * 100, 0).toFixed(0)}%
                    </div>
                </div>
            </div>

            <div className="row text-center">
                <div className="col-md-4">
                    <div className="card">
                        <h5 className="card-title mt-3 f-w-900">Income & Expense</h5>
                        <div ref={incomeChartRef} style={{ height: "350px", marginTop: '10px' }}></div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="card">
                        <h5 className="card-title mt-3 f-w-900">Bad Debt / Good Debt</h5>
                        <div ref={expenseChartRef} style={{ height: "350px", marginTop: '10px' }}></div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="card">
                        <h5 className="card-title mt-3 f-w-900">Assets & Liabilities</h5>
                        <div ref={savingsChartRef} style={{ height: "350px", marginTop: '10px' }}></div>
                    </div>
                </div>
            </div>
            <div className="row mt-4">
                <div className="col-md-4 d-flex flex-column mb-3" style={{ minHeight: '100%' }}>
                    <table className="custom-table flex-grow-1">
                        <thead style={{ backgroundColor: '#0000ff', color: 'white' }}>
                            <tr>
                                <th>
                                    <h3 style={{ margin: 0, textAlign: 'center' }}>CASHFLOW</h3>
                                    <h6 className="p-1 text-center">Income & Expense Details</h6>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Label>Income:</Label>
                                        <span>${formatNumberWithCommas(dashboardData[4]?.amount || 0)}</span>
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Label>Expense:</Label>
                                        <span>${formatNumberWithCommas(dashboardData[3]?.amount || 0)}</span>
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Label className="f-w-900">Cashflow:</Label>
                                        <span className="f-w-900">${formatNumberWithCommas(dashboardData[5]?.amount || 0)}</span>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="col-md-4 d-flex flex-column mb-3" style={{ minHeight: '100%' }}>
                    <table className="custom-table flex-grow-1">
                        <thead style={{ backgroundColor: '#000000', color: 'white' }}>
                            <tr>
                                <th>
                                    <h3 style={{ margin: 0, textAlign: 'center' }}>DEBT MANAGEMENT</h3>
                                    <h6 className="p-1 text-center">Bad Debt / Good Debt </h6>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Label>Bad Debt:</Label>
                                        <span>${formatNumberWithCommas(dashboardData[0]?.amount || 0)}</span>
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Label>Good Debt:</Label>
                                        <span>${formatNumberWithCommas(dashboardData[1]?.amount || 0)}</span>
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Label className="f-w-900">Total Debt:</Label>
                                        <span className="f-w-900">${formatNumberWithCommas(dashboardData[2]?.amount || 0)}</span>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="col-md-4 d-flex flex-column mb-3" style={{ minHeight: '100%' }}>
                    <table className="custom-table flex-grow-1">
                        <thead style={{ backgroundColor: '#ffad33', color: 'white' }}>
                            <tr>
                                <th>
                                    <h3 style={{ margin: 0, textAlign: 'center' }}>NET WORTH</h3>
                                    <h6 className="p-1 text-center">Assets & Liabilities</h6>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Label>Total Assets:</Label>
                                        <span>${formatNumberWithCommas(dashboardData[6]?.amount || 0)}</span>
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Label>Total Liabilities:</Label>
                                        <span>${formatNumberWithCommas(dashboardData[7]?.amount || 0)}</span>
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Label className="f-w-900">Net Worth:</Label>
                                        <span className="f-w-900">${formatNumberWithCommas(dashboardData[8]?.amount || 0)}</span>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <IncomeAndExpense dashboardData={dashboardData} />
        </div>
    );
};

export default Dashboard;